/**
 * Reports screen — folder-scoped report viewer.
 * Accessible from the folder list screen via "Reports" button.
 */
import { reportService } from '../services/api';
import { getState, loadFolders } from '../services/state';
import { sanitize } from '../services/utils';

let _container: HTMLElement;
let _folderId: string;
let _reports: any[] = [];
let _loading = false;

const REPORT_META: Record<string, { label: string; icon: string; desc: string; color: string }> = {
  folder_summary: {
    label: 'Folder Summary',
    icon: '<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',
    desc: 'Overall worksheet statistics, status breakdown, and recent activity.',
    color: '#3b82f6',
  },
  vendor_frequency: {
    label: 'Vendor Frequency',
    icon: '<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>',
    desc: 'Vendor usage across all worksheets — frequency, actor types, and status distribution.',
    color: '#f59e0b',
  },
  control_coverage: {
    label: 'Control Coverage',
    icon: '<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>',
    desc: 'Security control adoption rate across worksheets.',
    color: '#10b981',
  },
  gap_analysis: {
    label: 'Gap Analysis',
    icon: '<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>',
    desc: 'Worksheets with missing actors, functional gaps, or disabled controls.',
    color: '#ef4444',
  },
};

const REPORT_ORDER = ['folder_summary', 'vendor_frequency', 'control_coverage', 'gap_analysis'];

export async function renderReports(container: HTMLElement, folderId: string): Promise<void> {
  _container = container;
  _folderId = folderId;

  if (getState().folders.length === 0) await loadFolders();

  await loadReports();
  render();
}

async function loadReports(): Promise<void> {
  try {
    const { reports } = await reportService.list(_folderId);
    _reports = reports;
  } catch {
    _reports = [];
  }
}

async function handleGenerate(type?: string): Promise<void> {
  if (_loading) return;
  _loading = true;
  render();
  try {
    await reportService.generate(_folderId, type);
    await loadReports();
  } catch (err: any) {
    console.error('Report generation failed:', err);
  }
  _loading = false;
  render();
}

function render(): void {
  const folder = getState().folders.find((f) => f._id === _folderId);
  const folderName = folder ? sanitize(folder.name) : 'Folder';

  // Map reports by type for quick lookup
  const reportMap = new Map<string, any>();
  for (const r of _reports) {
    reportMap.set(r.report_type, r);
  }

  _container.innerHTML = `
    <div style="max-width:1000px;margin:0 auto;padding:12px 24px">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:16px">
        <button id="reports-back" style="color:var(--colorTextLight);background:none;border:none;cursor:pointer;padding:0;line-height:0;flex-shrink:0">
          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
        </button>
        <span style="font-size:14px;font-weight:600;color:var(--colorTextHeading)">Reports</span>
        <span style="font-size:11px;color:var(--colorTextMuted)">— ${folderName}</span>
        <div style="margin-left:auto;display:flex;gap:8px">
          <button id="generate-all-btn" style="padding:5px 12px;border-radius:6px;font-size:11px;font-weight:500;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)" ${_loading ? 'disabled' : ''}>
            ${_loading ? 'Generating…' : 'Generate All'}
          </button>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(460px,1fr));gap:16px" id="report-cards">
        ${REPORT_ORDER.map((type) => renderReportCard(type, reportMap.get(type))).join('')}
      </div>
    </div>
  `;

  // Events
  document.getElementById('reports-back')?.addEventListener('click', () => {
    window.location.hash = `folder/${_folderId}`;
  });

  document.getElementById('generate-all-btn')?.addEventListener('click', () => handleGenerate());

  document.querySelectorAll('[data-gen-type]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const type = (btn as HTMLElement).dataset.genType!;
      handleGenerate(type);
    });
  });

  // Expand/collapse
  document.querySelectorAll('[data-toggle-report]').forEach((el) => {
    el.addEventListener('click', () => {
      const target = (el as HTMLElement).dataset.toggleReport!;
      const detail = document.getElementById(`report-detail-${target}`);
      const arrow = document.getElementById(`report-arrow-${target}`);
      if (detail) {
        const open = detail.style.display !== 'none';
        detail.style.display = open ? 'none' : 'block';
        if (arrow) arrow.style.transform = open ? 'rotate(0deg)' : 'rotate(90deg)';
      }
    });
  });
}

function renderReportCard(type: string, report: any): string {
  const meta = REPORT_META[type];
  if (!meta) return '';

  const hasData = !!report && report.data && Object.keys(report.data).length > 0;
  const stale = report?.stale;
  const genAt = report?.generated_at ? new Date(report.generated_at).toLocaleString() : null;

  return `
    <div style="background:var(--colorBgCard);border:1px solid var(--colorBorder);border-radius:var(--radiusLg);overflow:hidden">
      <div data-toggle-report="${type}" style="display:flex;align-items:center;gap:10px;padding:14px 16px;cursor:pointer;user-select:none">
        <div style="color:${meta.color};flex-shrink:0">${meta.icon}</div>
        <div style="flex:1;min-width:0">
          <div style="font-size:13px;font-weight:600;color:var(--colorTextHeading)">${meta.label}</div>
          <div style="font-size:10px;color:var(--colorTextMuted);margin-top:2px">${meta.desc}</div>
        </div>
        ${stale ? '<span style="font-size:9px;padding:2px 6px;border-radius:4px;background:#fef3c7;color:#92400e;font-weight:500">STALE</span>' : ''}
        ${hasData && genAt ? `<span style="font-size:9px;color:var(--colorTextLight);white-space:nowrap">${genAt}</span>` : ''}
        <button data-gen-type="${type}" style="padding:3px 8px;border-radius:4px;font-size:10px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText);flex-shrink:0" ${_loading ? 'disabled' : ''} title="Generate this report">
          ${_loading ? '…' : '↻'}
        </button>
        <svg id="report-arrow-${type}" width="14" height="14" fill="none" stroke="var(--colorTextLight)" viewBox="0 0 24 24" stroke-width="2" style="flex-shrink:0;transition:transform .15s;transform:${hasData ? 'rotate(90deg)' : 'rotate(0deg)'}"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/></svg>
      </div>
      <div id="report-detail-${type}" style="display:${hasData ? 'block' : 'none'};border-top:1px solid var(--colorBorder);padding:16px;font-size:12px;color:var(--colorText)">
        ${hasData ? renderReportDetail(type, report.data) : '<div style="color:var(--colorTextMuted);font-style:italic">No data yet — click generate.</div>'}
      </div>
    </div>
  `;
}

// ─── Detail Renderers ─────────────────────────────────

function renderReportDetail(type: string, data: any): string {
  switch (type) {
    case 'folder_summary': return renderFolderSummary(data);
    case 'vendor_frequency': return renderVendorFrequency(data);
    case 'control_coverage': return renderControlCoverage(data);
    case 'gap_analysis': return renderGapAnalysis(data);
    default: return '<pre style="font-size:10px;overflow:auto">' + sanitize(JSON.stringify(data, null, 2)) + '</pre>';
  }
}

function renderFolderSummary(d: any): string {
  const statusColors: Record<string, string> = {
    draft: '#64748b', 'in-progress': '#3b82f6', 'in-review': '#f59e0b',
    complete: '#10b981', archived: '#8b5cf6',
  };

  const statusBars = Object.entries(d.status_counts || {}).map(([status, count]) => {
    const pct = d.total_worksheets > 0 ? ((count as number) / d.total_worksheets * 100) : 0;
    const color = statusColors[status] || '#94a3b8';
    return `
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
        <span style="width:80px;font-size:11px;text-transform:capitalize;color:var(--colorTextMuted)">${status.replace('-', ' ')}</span>
        <div style="flex:1;height:8px;border-radius:4px;background:var(--colorBgElevated);overflow:hidden">
          <div style="width:${pct}%;height:100%;border-radius:4px;background:${color}"></div>
        </div>
        <span style="width:30px;text-align:right;font-size:11px;font-weight:500">${count}</span>
      </div>
    `;
  }).join('');

  const actorStatusPills = Object.entries(d.actor_status_counts || {}).map(([status, count]) => {
    const colors: Record<string, string> = {
      'in-place': '#10b981', planned: '#3b82f6', missing: '#ef4444',
      issue: '#f59e0b', 'func-gap': '#f97316', 'not-needed': '#94a3b8',
    };
    return `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:10px;font-size:10px;background:${colors[status] || '#94a3b8'}22;color:${colors[status] || '#94a3b8'};font-weight:500">${status.replace('-', ' ')} ${count}</span>`;
  }).join(' ');

  const recentRows = (d.recent_activity || []).map((r: any) => `
    <tr>
      <td style="padding:3px 8px;font-size:11px;color:var(--colorText)">${sanitize(r.name)}</td>
      <td style="padding:3px 8px;font-size:10px;color:var(--colorTextMuted);text-align:right">${new Date(r.updated_at).toLocaleDateString()}</td>
    </tr>
  `).join('');

  return `
    <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px">
      ${statBox('Worksheets', d.total_worksheets, '#3b82f6')}
      ${statBox('Actors', d.total_actors, '#f59e0b')}
      ${statBox('Credentials', d.total_credentials, '#8b5cf6')}
      ${statBox('Avg Actors/WS', d.avg_actors_per_worksheet, '#10b981')}
    </div>
    <div style="margin-bottom:16px">
      <div style="font-size:11px;font-weight:600;color:var(--colorTextHeading);margin-bottom:8px">Status Distribution</div>
      ${statusBars}
    </div>
    <div style="margin-bottom:16px">
      <div style="font-size:11px;font-weight:600;color:var(--colorTextHeading);margin-bottom:8px">Actor Statuses</div>
      <div style="display:flex;flex-wrap:wrap;gap:6px">${actorStatusPills}</div>
    </div>
    ${recentRows ? `
    <div>
      <div style="font-size:11px;font-weight:600;color:var(--colorTextHeading);margin-bottom:6px">Recent Activity</div>
      <table style="width:100%;border-collapse:collapse"><tbody>${recentRows}</tbody></table>
    </div>` : ''}
  `;
}

function renderVendorFrequency(d: any): string {
  if (!d.vendors || d.vendors.length === 0) {
    return '<div style="color:var(--colorTextMuted)">No vendors assigned across worksheets.</div>';
  }

  const maxCount = d.vendors[0]?.count || 1;
  const rows = d.vendors.map((v: any) => {
    const pct = (v.count / maxCount) * 100;
    const statusPills = Object.entries(v.statuses || {}).map(([s, c]) =>
      `<span style="font-size:9px;padding:1px 5px;border-radius:8px;background:var(--colorBgElevated);color:var(--colorTextMuted)">${s} ${c}</span>`
    ).join(' ');

    return `
      <div style="margin-bottom:10px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px">
          <span style="font-size:12px;font-weight:500;color:var(--colorText);min-width:120px">${sanitize(v.vendor)}</span>
          <div style="flex:1;height:6px;border-radius:3px;background:var(--colorBgElevated);overflow:hidden">
            <div style="width:${pct}%;height:100%;border-radius:3px;background:#f59e0b"></div>
          </div>
          <span style="font-size:11px;font-weight:600;color:var(--colorTextHeading);min-width:24px;text-align:right">${v.count}</span>
        </div>
        <div style="margin-left:128px;display:flex;gap:4px;flex-wrap:wrap">${statusPills}</div>
      </div>
    `;
  }).join('');

  return `
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px">
      ${statBox('Total Actors', d.total_actors, '#f59e0b')}
      ${statBox('Unique Vendors', d.vendors.length, '#3b82f6')}
      ${statBox('Unassigned', d.unassigned_count, '#94a3b8')}
    </div>
    <div>${rows}</div>
  `;
}

function renderControlCoverage(d: any): string {
  if (!d.controls || d.controls.length === 0) {
    return '<div style="color:var(--colorTextMuted)">No controls tracked across worksheets.</div>';
  }

  const rows = d.controls.slice(0, 30).map((c: any) => {
    const color = c.coverage_pct >= 75 ? '#10b981' : c.coverage_pct >= 40 ? '#f59e0b' : '#ef4444';
    return `
      <tr>
        <td style="padding:4px 8px;font-size:11px;color:var(--colorText)">${sanitize(c.control_id)}</td>
        <td style="padding:4px 8px;text-align:center;font-size:11px">${c.enabled_count}/${c.total_worksheets}</td>
        <td style="padding:4px 8px;width:120px">
          <div style="display:flex;align-items:center;gap:6px">
            <div style="flex:1;height:6px;border-radius:3px;background:var(--colorBgElevated);overflow:hidden">
              <div style="width:${c.coverage_pct}%;height:100%;border-radius:3px;background:${color}"></div>
            </div>
            <span style="font-size:10px;font-weight:500;color:${color}">${c.coverage_pct}%</span>
          </div>
        </td>
      </tr>
    `;
  }).join('');

  return `
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px">
      ${statBox('Controls Tracked', d.total_controls, '#10b981')}
      ${statBox('Avg Coverage', d.avg_coverage_pct + '%', '#3b82f6')}
      ${statBox('Worksheets', d.total_worksheets, '#8b5cf6')}
    </div>
    <table style="width:100%;border-collapse:collapse">
      <thead>
        <tr style="border-bottom:1px solid var(--colorBorder)">
          <th style="padding:4px 8px;text-align:left;font-size:10px;font-weight:600;color:var(--colorTextMuted);text-transform:uppercase">Control</th>
          <th style="padding:4px 8px;text-align:center;font-size:10px;font-weight:600;color:var(--colorTextMuted);text-transform:uppercase">Enabled</th>
          <th style="padding:4px 8px;text-align:left;font-size:10px;font-weight:600;color:var(--colorTextMuted);text-transform:uppercase">Coverage</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
    ${d.controls.length > 30 ? `<div style="font-size:10px;color:var(--colorTextMuted);margin-top:8px;text-align:center">Showing 30 of ${d.controls.length} controls</div>` : ''}
  `;
}

function renderGapAnalysis(d: any): string {
  if (!d.gaps || d.gaps.length === 0) {
    return '<div style="color:var(--colorTextMuted)">No gaps detected — all worksheets look good.</div>';
  }

  const rows = d.gaps.map((g: any) => {
    const actorPills = (g.missing_actors || []).map((a: any) => {
      const colors: Record<string, string> = { missing: '#ef4444', issue: '#f59e0b', 'func-gap': '#f97316' };
      const c = colors[a.status] || '#94a3b8';
      return `<span style="font-size:9px;padding:1px 6px;border-radius:8px;background:${c}22;color:${c}">${sanitize(a.type)} (${a.status})</span>`;
    }).join(' ');

    const ctrlCount = g.disabled_control_count || 0;

    return `
      <div style="padding:10px 0;border-bottom:1px solid var(--colorBorder)">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px">
          <span style="font-size:12px;font-weight:500;color:var(--colorText)">${sanitize(g.worksheet_name)}</span>
          <div style="display:flex;gap:8px">
            ${g.missing_actor_count ? `<span style="font-size:10px;color:#ef4444;font-weight:500">${g.missing_actor_count} actor gap${g.missing_actor_count > 1 ? 's' : ''}</span>` : ''}
            ${ctrlCount ? `<span style="font-size:10px;color:#f59e0b;font-weight:500">${ctrlCount} control${ctrlCount > 1 ? 's' : ''} disabled</span>` : ''}
          </div>
        </div>
        ${actorPills ? `<div style="display:flex;flex-wrap:wrap;gap:4px">${actorPills}</div>` : ''}
      </div>
    `;
  }).join('');

  return `
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px">
      ${statBox('Total Worksheets', d.total_worksheets, '#3b82f6')}
      ${statBox('With Gaps', d.worksheets_with_gaps, '#ef4444')}
      ${statBox('Clean', d.total_worksheets - d.worksheets_with_gaps, '#10b981')}
    </div>
    <div>${rows}</div>
  `;
}

// ─── Helpers ──────────────────────────────────────────

function statBox(label: string, value: string | number, color: string): string {
  return `
    <div style="background:var(--colorBgElevated);border-radius:var(--radiusMd);padding:10px 12px;text-align:center">
      <div style="font-size:18px;font-weight:700;color:${color}">${value}</div>
      <div style="font-size:9px;color:var(--colorTextMuted);margin-top:2px;text-transform:uppercase;letter-spacing:.05em">${label}</div>
    </div>
  `;
}
